import {action, computed, makeObservable, observable, runInAction} from 'mobx'

import {Chats, UploadChatMessageParams, globalChatsStore} from 'chats'
import type {LocationModel} from 'front-api'
import {
  chatEventEmitter,
  deleteMessageIn,
  deleteMessagesIn,
  replaceMessageIn,
} from './connect-chat'
import {addToSet, firstOf, lastOf, sortMessagesByDate} from './connect-helpers'
import type {ChatData, ChatMessage} from './models'
import {MESSAGE_TYPE_IMAGE, MESSAGE_TYPE_VIDEO} from './models'
import {chatsWebsockets} from './socket.io/ChatsWebsockets'

export class ChatStore {
  chat: ChatData
  private isReachedEnd = false
  private currentIds = new Set<string>()

  constructor(chat: ChatData, private userId: string) {
    this.chat = chat
    makeObservable(this)
    chatEventEmitter.on('newMessage', this.onNewMessage)
    chatEventEmitter.on('onMessageRead', this.onMessageRead)
    chatEventEmitter.on('onMessageReceived', this.onMessageReceived)
  }

  destroy() {
    chatEventEmitter.off('newMessage', this.onNewMessage)
    chatEventEmitter.off('onMessageRead', this.onMessageRead)
    chatEventEmitter.off('onMessageReceived', this.onMessageReceived)
  }

  @observable
  messages: Array<ChatMessage> = []

  @observable
  isFetching = false

  @observable
  isSending = false

  @observable
  selectMode = false

  @observable
  replyMessage: ChatMessage | undefined

  @observable
  selectedItems = new Set<string>()

  lastReadId = '-2'

  @computed
  get selectionInfo(): {
    selectMode: boolean
    selectedItems: Set<string>
  } {
    const selectMode = this.selectMode
    const selectedItems = selectMode ? this.selectedItems : new Set<string>()
    return {selectMode, selectedItems}
  }

  @computed
  get selectionInfoOfStrictlyOneItem(): {id?: string} {
    const isSelected = this.selectMode && this.selectedItems.size === 1
    const [id] = isSelected ? this.selectedItems : []
    return {id}
  }

  sendMessage = async (text: string) => {
    await chatsWebsockets.sendTextMessage(this.chat.id, text)
  }

  sendLocationMessage = async (location: LocationModel) => {
    await chatsWebsockets.sendLocationMessage(
      this.chat.id,
      location.latitude,
      location.longitude,
    )
  }

  sendImageMessage = async (url: string | Blob) => {
    const endpoint = 'files/image-chat/upload'
    const params: UploadChatMessageParams = {
      chat: this.chat.id,
      user: this.userId,
      type: MESSAGE_TYPE_IMAGE.toString(10),
    }
    const data =
      typeof url === 'string' ? Chats.system.mediaPathMapper(url) : url
    Chats.system.sendMedia(endpoint, 'image', data, params)
  }

  sendVideoMessage = async (url: string | Blob) => {
    const endpoint = 'files/video-chat/upload'
    const params: UploadChatMessageParams = {
      chat: this.chat.id,
      user: this.userId,
      type: MESSAGE_TYPE_VIDEO.toString(10),
    }
    const data =
      typeof url === 'string' ? Chats.system.mediaPathMapper(url) : url
    Chats.system.sendMedia(endpoint, 'video', data, params)
  }

  sendFileMessage = async (url: string | Blob) => {
    const endpoint = 'files/file-chat/upload'
    const params: UploadChatMessageParams = {
      chat: this.chat.id,
      user: this.userId,
    }
    const data =
      typeof url === 'string' ? Chats.system.mediaPathMapper(url) : url
    Chats.system.sendMedia(endpoint, 'file', data, params)
  }

  @action
  fetchInitialMessages = async () => {
    console.log('fetchInitialMessages', this.chat.id)
    const chat = await chatsWebsockets.fetchChat(this.chat.id)

    runInAction(() => {
      if (chat) {
        this.chat = chat
      }
      if (this.isFetching) return
      if (this.messages.length === 0 && this.chat.lastMessage !== undefined) {
        this.messages = [this.chat.lastMessage]
        if (this.messages.length < 2) this.fetchBefore()
      } else {
        this.fetchBefore()
      }
    })
  }

  fetchBefore = () => {
    if (this.isReachedEnd) return
    if (this.isFetching) return
    return new Promise<any>(async (resolve) => {
      this.isFetching = true

      const lastMessage = lastOf(this.messages)

      const response = await chatsWebsockets.fetchMessages({
        chat_id: this.chat.id,
        since: lastMessage?.date ?? 9999999999999,
      })

      if (!response) {
        runInAction(() => (this.isFetching = false))
        return
      }

      if (response.length === 0) {
        this.isReachedEnd = true
        runInAction(() => (this.isFetching = false))
        return
      }

      const messages: Array<ChatMessage> = []
      for (let message of response) {
        if (addToSet(this.currentIds, message.localId)) {
          messages.push(message)
        }
      }

      runInAction(() => {
        this.messages = this.messages
          .concat(messages)
          .sort(sortMessagesByDate)
          .reverse()
        this.isReachedEnd = true
        this.isFetching = false
        // take first because list is reversed
        const message = firstOf(this.messages)
        if (!message) return
        // this.setReceived(message.localId)
        this.setSeen(message.localId)
        resolve(undefined)
      })
    })
  }

  @action
  onNewMessage = async (message: ChatMessage) => {
    runInAction(() => {
      if (!this.chat) return
      if (this.chat.id !== message.chatId) return
      // must be called first
      this.setSeen(message.localId)
      this.lastReadId = message.localId
      // update delivered status
      if (!addToSet(this.currentIds, message.localId)) {
        this.messages = replaceMessageIn(this.messages, message)
      } else {
        // insert new message
        this.messages = [message, ...this.messages]
      }
    })
  }

  @action
  onMessageRead = (messageId: string) => {
    // console.log('🔦 Read', messageId)
    const index = this.messages.findIndex((f) => f.localId === messageId)
    if (index === -1) return
    const msgsForMarking = this.messages.slice(index, this.messages.length - 1)
    if (msgsForMarking.length === 0) return
    msgsForMarking.forEach((m) => {
      if (m.ownerId === this.userId) m.isRead = true
    })
  }

  @action
  onMessageReceived = ({
    localId,
    chatId,
    id,
  }: {
    localId: string
    chatId: string
    id: string
  }) => {
    console.log('🔦 Received', localId, id)
    if (this.chat.id !== chatId) return
    const message = this.messages.find((f) => f.localId === localId)
    if (!message) return
    message.id = id
    message.isDelivered = true
  }

  @action
  setReceived = async (messageId: string) => {
    if (this.lastReadId === messageId) return
    if (this.lastReadId > messageId) return
    await chatsWebsockets.sendReceived(messageId)
  }

  @action
  setSeen = (messageId: string) => {
    if (this.lastReadId === messageId) return
    if (this.lastReadId > messageId) return
    chatsWebsockets.sendSeen({messageId, chatId: this.chat.id})
    globalChatsStore.updateUnreadMessageCountForChat(this.chat.id)
  }

  @action
  setReplyMessage = (messageId: string | undefined) => {
    if (!messageId) {
      this.replyMessage = undefined
      return
    }
    const message = this.messages.find((m) => m.localId === messageId)
    if (!message) return
    if (this.selectMode) this.toggleSelectMode()
    this.replyMessage = message
  }

  @action
  toggleSelectMode = () => {
    if (this.replyMessage) this.setReplyMessage(undefined)
    this.selectMode = !this.selectMode
    this.selectedItems.clear()
  }

  @action
  setItemSelected = (messageId: string, selected: boolean) => {
    if (selected) {
      this.selectedItems.add(messageId)
    } else {
      this.selectedItems.delete(messageId)
    }
  }

  @action
  deleteMessage = (
    messageId: string,
    options: {full: boolean},
    removeLocally: boolean = true,
  ) => {
    chatsWebsockets.deleteMessage(messageId, options.full)
    if (removeLocally) {
      this.selectedItems.delete(messageId)
      this.messages = deleteMessageIn(this.messages, messageId)
    }
  }

  @action
  deleteMessages = (messageIds: Set<string>, options: {full: boolean}) => {
    if (messageIds.size === 0) return
    messageIds.forEach((messageId) =>
      this.deleteMessage(messageId, options, false),
    )
    messageIds.forEach((id) => this.selectedItems.delete(id))
    this.messages = deleteMessagesIn(this.messages, messageIds)
  }

  @action
  translateMessage = (messageId: string, translated: boolean | undefined) => {
    const message = this.messages.find((m) => m.localId === messageId)
    if (!message) return
    message.localTranslated = translated
  }

  @action
  resetLocalTranslatedMessages = () => {
    this.messages.forEach((message) => {
      message.localTranslated = undefined
    })
  }
}
